import { LoadingBox } from '@revelio/core';
import { Button, DetailsWithDescription, Option, Text } from '@revelio/ui';

import { useDataBuilderQuery } from '../api/useDataBuilderQuery';
import { DataBuilderFooter } from '../components/DataBuilderFooter';
import { DataBuilderGrid } from '../components/ui/DataBuilderGrid';
import { DataBuilderOption } from '../components/ui/DataBuilderOption';
import { DataBuilderStepLayout } from '../components/ui/DataBuilderStepLayout';
import {
  selectConfigByInstance,
  selectDatasetInstances,
  selectModelVersions,
  selectSelectedDatasetKeys,
  useDataBuilderActions,
  useDataBuilderSelector,
} from '../data-builder.store';
import {
  type DataBuilderDataset,
  groupDatasetsByGroupId,
} from '../datasets/api-transform';
import { getDeliverableColumnConfig } from '../datasets/deliverable-column-config';
import { seedInstanceDefaults } from '../datasets/initial-instance-config';
import {
  applyInheritedUoa,
  pickLastUsedUoaForTarget,
} from '../unit-of-analysis-defaulting';
import { useDataBuilderNavigation } from '../useDataBuilderNavigation';
import styles from './step1-dataset.module.css';
import {
  buildConditionContext,
  filterUnitOptionsByModels,
} from './step2-column/filter-config-by-models';

type DatasetCardProps = {
  dataset: DataBuilderDataset;
  isSelected: boolean;
  onClick: () => void;
};

const DatasetCard = ({ dataset, isSelected, onClick }: DatasetCardProps) => {
  return (
    <Option
      data-testid={`data-builder-dataset-option-${dataset.id}`}
      className={styles.datasetCard}
      disabled={dataset.isDisabled}
      isSelected={isSelected}
      onClick={onClick}
      startSlot={
        <div className={styles.checkboxSlot}>
          <DataBuilderOption.CheckBox isSelected={isSelected} />
        </div>
      }
      contentAlignment="start"
      startSlotAlignment="start"
    >
      <DetailsWithDescription
        title={dataset.label}
        detail={dataset.detail ?? ''}
        disabled={dataset.isDisabled}
      />
    </Option>
  );
};

export const DataBuilderDatasetStep = () => {
  const { data, isLoading } = useDataBuilderQuery();
  const selectedDatasetKeys = useDataBuilderSelector(selectSelectedDatasetKeys);
  const instances = useDataBuilderSelector(selectDatasetInstances);
  const configByInstance = useDataBuilderSelector(selectConfigByInstance);
  const modelVersions = useDataBuilderSelector(selectModelVersions);
  const {
    toggleDatasetType,
    addDatasetInstance,
    setUnitOfAnalysis,
    setUnitOfAnalysisSelections,
    setCompanySelections,
    setCompanySets,
    setCompanyMapper,
  } = useDataBuilderActions();
  const { goToNextStep } = useDataBuilderNavigation();

  const datasetsByGroup =
    data !== undefined ? groupDatasetsByGroupId(data.datasets) : new Map();

  // Seed file/column defaults at toggle time so every instance enters the
  // store fully populated — Step 2 no longer needs to materialize defaults
  // in an effect, and the submit path can read state as-is.
  const handleToggle = (datasetKey: string) => {
    if (selectedDatasetKeys.includes(datasetKey)) {
      toggleDatasetType(datasetKey);
      return;
    }

    // Adding a new dataset from the selection page should inherit the Unit of
    // Analysis from existing datasets.
    const existingInstanceIds = instances.map((inst) => inst.instanceId);
    const deliverableConfig = getDeliverableColumnConfig(
      data?.deliverablesConfig ?? {},
      datasetKey
    );
    const defaultSettings = Object.fromEntries(
      (deliverableConfig?.settings ?? []).map((setting) => [
        setting.key,
        setting.default,
      ])
    );
    const conditionContext = buildConditionContext(
      modelVersions,
      null,
      defaultSettings
    );
    const initialConfig =
      deliverableConfig !== undefined
        ? {
            ...seedInstanceDefaults(deliverableConfig, conditionContext),
            settings: defaultSettings,
          }
        : undefined;
    const newId = addDatasetInstance(datasetKey, initialConfig);

    const availableValues = filterUnitOptionsByModels(
      deliverableConfig?.unitOfAnalysis.options ?? [],
      conditionContext
    ).map((option) => option.value);
    const pickedUoa = pickLastUsedUoaForTarget({
      configByInstance,
      orderedInstanceIds: existingInstanceIds,
      availableUoaValues: availableValues,
    });
    if (pickedUoa !== null) {
      applyInheritedUoa(
        {
          setUnitOfAnalysis,
          setUnitOfAnalysisSelections,
          setCompanySelections,
          setCompanySets,
          setCompanyMapper,
        },
        newId,
        pickedUoa,
        configByInstance[pickedUoa.sourceInstanceId]
      );
    }
  };

  return (
    <DataBuilderStepLayout
      title="Select Datasets"
      footer={
        <DataBuilderFooter
          actions={
            <Button
              onClick={goToNextStep}
              disabled={instances.length === 0}
              color="primary"
              size="sm"
            >
              Continue
            </Button>
          }
        />
      }
    >
      <LoadingBox loading={isLoading}>
        <div className={styles.groups}>
          {data?.groups.map((group) => {
            const groupDatasets = datasetsByGroup.get(group.id);
            if (!groupDatasets?.length) return null;
            return (
              <section
                key={group.id}
                className={styles.group}
                data-testid={`data-builder-dataset-group-${group.id}`}
              >
                <Text
                  as="h3"
                  fontSize="sm"
                  weight="semibold"
                  className={styles.groupHeading}
                >
                  {group.name}
                </Text>
                <DataBuilderGrid columns="2">
                  {groupDatasets.map((dataset: DataBuilderDataset) => (
                    <DatasetCard
                      key={dataset.id}
                      dataset={dataset}
                      isSelected={selectedDatasetKeys.includes(dataset.id)}
                      onClick={() => handleToggle(dataset.id)}
                    />
                  ))}
                </DataBuilderGrid>
              </section>
            );
          })}
        </div>
      </LoadingBox>
    </DataBuilderStepLayout>
  );
};
